import axios from 'axios';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://31.220.103.204:3000/';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login if unauthorized
      localStorage.removeItem('token');
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/book/') &&
          !window.location.hash.includes('session_id')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

export const companyAPI = {
  create: (data) => api.post('/companies', data),
  getMyCompany: () => api.get('/companies/me'),
  update: (data) => api.put('/companies/me', data),
};

export const branchAPI = {
  create: (data) => api.post('/branches', data),
  getAll: () => api.get('/branches'),
  getOne: (id) => api.get(`/branches/${id}`),
  updateHours: (id, data) => api.put(`/branches/${id}/hours`, data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  toggle: (id) => api.patch(`/branches/${id}/toggle`),
  toggleAppointments: (id) => api.patch(`/branches/${id}/toggle-appointments`),
};

export const barberAPI = {
  create: (data) => api.post('/barbers', data),
  getAll: (branchId) => api.get('/barbers', { params: { branch_id: branchId } }),
  getOne: (id) => api.get(`/barbers/${id}`),
  update: (id, data) => api.put(`/barbers/${id}`, data),
  getEarnings: (id, period) => api.get(`/barbers/${id}/earnings`, { params: { period } }),
  toggle: (id) => api.patch(`/barbers/${id}/toggle`),
};

export const serviceAPI = {
  create: (data) => api.post('/services', data),
  getAll: (params) => api.get('/services', { params }),
  update: (id, data) => api.put(`/services/${id}`, data),
  downloadTemplate: () => api.get('/services/template', { responseType: 'blob' }),
  bulkUpload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/services/bulk-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  toggle: (id) => api.patch(`/services/${id}/toggle`),
};

export const expenseAPI = {
  create: (data) => api.post('/expenses', data),
  getAll: (params) => api.get('/expenses', { params }),
  void: (id) => api.post(`/expenses/${id}/void`),
};

export const clientAPI = {
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  getAll: (search) => api.get('/clients', { params: { search } }),
  getOne: (id) => api.get(`/clients/${id}`),
  getHistory: (id) => api.get(`/clients/${id}/history`),
};

export const appointmentAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: (params) => api.get('/appointments', { params }),
  getOne: (id) => api.get(`/appointments/${id}`),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  updateStatus: (id, status) => api.put(`/appointments/${id}/status`, { status }),
  confirm: (id, advance_amount) => api.put(`/appointments/${id}/confirm`, { advance_amount }),
  getAvailableSlots: (params) => api.get('/appointments/available-slots', { params }),
  reschedule: (id, data) => api.put(`/appointments/${id}/reschedule`, data),
};

export const saleAPI = {
  create: (data) => api.post('/sales', data),
  getAll: (params) => api.get('/sales', { params }),
  getOne: (id) => api.get(`/sales/${id}`),
  sendReceipt: (id, email) => api.post(`/sales/${id}/send-receipt`, { email }),
  void: (id) => api.post(`/sales/${id}/void`),
  getReceipt: (id) => api.get(`/sales/${id}/receipt`),
};

export const payoutAPI = {
  create: (data) => api.post('/payouts', data),
  getAll: (barberId) => api.get('/payouts', { params: { barber_id: barberId } }),
  markPaid: (id) => api.put(`/payouts/${id}/mark-paid`),
  preview: (data) => api.post('/payouts/preview', data),
  createEnhanced: (data) => api.post('/payouts/create-enhanced', data),
};

export const productAPI = {
  create: (data) => api.post('/products', data),
  getAll: (params) => api.get('/products', { params }),
  updateStock: (id, adjustment, reason) => api.put(`/products/${id}/stock`, { adjustment, reason }),
  downloadTemplate: () => api.get('/products/template', { responseType: 'blob' }),
  bulkUpload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/products/bulk-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => api.put(`/products/${id}`, data),
  toggle: (id) => api.patch(`/products/${id}/toggle`),
};

export const holidayAPI = {
  create: (data) => api.post('/holidays', data),
  getAll: () => api.get('/holidays'),
};

export const fiscalAPI = {
  getAll: () => api.get('/fiscal-types'),
  create: (data) => api.post('/fiscal-types', data),
  update: (id, data) => api.put(`/fiscal-types/${id}`, data),
};

export const rncAPI = {
  lookup: (rnc) => api.get('/rnc/lookup', { params: { rnc } }),
};

export const dashboardAPI = {
  getOwnerDashboard: () => api.get('/dashboard/owner'),
  getCashierDashboard: () => api.get('/dashboard/cashier'),
  getBarberDashboard: (barberId) => api.get(`/dashboard/barber/${barberId}`),
};

export const reportAPI = {
  getRevenue: (params) => api.get('/reports/revenue', { params }),
  getServices: (period) => api.get('/reports/services', { params: { period } }),
  getBarbers: (period) => api.get('/reports/barbers', { params: { period } }),
};

export const salesAPI = {
  getHistory: (params) => api.get('/sales/history', { params }),
  getDailyClosing: (date, branch_id) => api.get('/cashier/daily-closing', { params: { date, branch_id } }),
  closeDay: (date, branch_id) => api.post('/cashier/close-day', { date, branch_id }),
};

export const publicAPI = {
  getCompany: (slug) => api.get(`/public/company/${slug}`),
  getServices: (slug, branchId) => api.get(`/public/company/${slug}/services`, { params: { branch_id: branchId } }),
  getBarbers: (slug, branchId) => api.get(`/public/company/${slug}/barbers`, { params: { branch_id: branchId } }),
  getSlots: (slug, params) => api.get(`/public/company/${slug}/slots`, { params }),
  book: (slug, data) => api.post(`/public/company/${slug}/book`, data),
};

export const staffAPI = {
  create: (data) => api.post('/staff', data),
  getAll: () => api.get('/staff'),
  toggle: (userId) => api.patch(`/staff/${userId}/toggle`),
  delete: (userId) => api.delete(`/staff/${userId}`),
};

export const serviceCategoryAPI = {
  getAll: () => api.get('/service-categories'),
  create: (data) => api.post('/service-categories', data),
  update: (id, data) => api.put(`/service-categories/${id}`, data),
  delete: (id) => api.delete(`/service-categories/${id}`),
  getHiddenBuiltins: () => api.get('/service-categories/hidden-builtins'),
  setHiddenBuiltins: (hidden) => api.put('/service-categories/hidden-builtins', { hidden }),
};

export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  addComment: (id, text) => api.post(`/leads/${id}/comments`, { text }),
  addTask: (id, data) => api.post(`/leads/${id}/tasks`, data),
  markTaskDone: (leadId, taskId) => api.patch(`/leads/${leadId}/tasks/${taskId}/done`),
  getTasksToday: () => api.get('/leads/tasks/today'),
  getTasksCalendar: (month) => api.get('/leads/tasks/calendar', { params: month ? { month } : {} }),
  getEarnings: () => api.get('/sales-partner/earnings'),
};

export const adminAPI = {
  getUsers: (search) => api.get('/admin/users', { params: search ? { search } : {} }),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  getSalesPerformance: () => api.get('/admin/sales-partners/performance'),
  listCompanies: () => api.get('/admin/companies'),
  toggleWhatsappPremium: (companyId, enabled) =>
    api.post(`/admin/companies/${companyId}/whatsapp-premium`, { enabled }),
  getWhatsappLog: (params) => api.get('/admin/whatsapp-log', { params }),
};

export const whatsappAPI = {
  connect: () => api.post('/whatsapp/connect'),
  getQr: () => api.get('/whatsapp/qr'),
  getStatus: () => api.get('/whatsapp/status'),
  // Lightweight, all-roles check (owner-only getStatus 403s for staff)
  getAvailable: () => api.get('/whatsapp/available'),
  disconnect: () => api.post('/whatsapp/disconnect'),
  sendTest: (phone) => api.post('/whatsapp/send-test', { phone }),
  getLog: (limit = 10) => api.get('/whatsapp/log', { params: { limit } }),
};

export const templateAPI = {
  getAll: () => api.get('/notification-templates'),
  update: (locale, data) => api.put(`/notification-templates/${locale}`, data),
};
